import {
  createContext, useCallback, useContext, useMemo, useState, type ReactNode,
} from 'react'
import { refreshCustomerSession } from './customerAuthApi'
import {
  clearPersistedIdentity,
  dispatchAuthChanged,
  persistIdentity,
  setRuntimeToken,
} from './authPersistence'
import {
  useCustomerAuthActions,
  useCustomerAuthBootstrap,
  useCustomerAuthStorageSync,
} from './hooks'
import type { CustomerLoginActionResult } from './hooks/useCustomerAuthActions'
import type {
  CustomerAccount,
  CustomerLoginInput,
  CustomerProfileUpdateInput,
  CustomerProfile,
  CustomerSignupInput,
} from './types'

type CustomerAuthContextValue = {
  token: string | null
  account: CustomerAccount | null
  profile: CustomerProfile | null
  authenticated: boolean
  initializing: boolean
  busy: boolean
  error: string
  clearError: () => void
  signup: (input: CustomerSignupInput) => Promise<boolean>
  login: (input: CustomerLoginInput) => Promise<CustomerLoginActionResult>
  verifyLoginSecondFactor: (input: { sessionId: string; code: string }) => Promise<CustomerLoginActionResult>
  verifyLoginSecondFactorWithPasskey: (input: { sessionId: string }) => Promise<CustomerLoginActionResult>
  updateProfile: (input: CustomerProfileUpdateInput) => Promise<boolean>
  logout: () => Promise<void>
  refresh: () => Promise<boolean>
}

const CustomerAuthContext = createContext<CustomerAuthContextValue | null>(null)

export const CustomerAuthProvider = ({ children }: { children: ReactNode }) => {
  const [token, setToken] = useState<string | null>(null)
  const [account, setAccount] = useState<CustomerAccount | null>(null)
  const [profile, setProfile] = useState<CustomerProfile | null>(null)
  const [initializing, setInitializing] = useState(true)
  const [busy, setBusy] = useState(false)
  const [error, setError] = useState('')
  const clearError = useCallback(() => setError(''), [])

  const applyAuthenticatedState = useCallback(
    (nextToken: string, nextAccount: CustomerAccount, nextProfile: CustomerProfile) => {
      setToken(nextToken)
      setAccount(nextAccount)
      setProfile(nextProfile)
      persistIdentity(nextToken, nextAccount.id)
      setRuntimeToken(nextToken)
      dispatchAuthChanged()
    },
    [],
  )

  const clearAuthState = useCallback(
    ({ clearStorage = true, broadcast = true }: { clearStorage?: boolean; broadcast?: boolean } = {}) => {
      setToken(null)
      setAccount(null)
      setProfile(null)
      if (clearStorage) {
        clearPersistedIdentity()
      }
      setRuntimeToken(null)
      if (broadcast) {
        dispatchAuthChanged()
      }
    },
    [],
  )

  const bootstrapFromSession = useCallback(
    async () => {
      const session = await refreshCustomerSession()
      applyAuthenticatedState(session.token, session.account, session.profile)
    },
    [applyAuthenticatedState],
  )

  useCustomerAuthBootstrap({
    applyAuthenticatedState,
    clearAuthState,
    setInitializing,
  })

  const {
    signup,
    login,
    verifyLoginSecondFactor,
    verifyLoginSecondFactorWithPasskey,
    refresh,
    logout,
    patchProfile,
  } = useCustomerAuthActions({
    token,
    applyAuthenticatedState,
    clearAuthState,
    setBusy,
    setError,
  })

  useCustomerAuthStorageSync({
    bootstrapFromSession,
    clearAuthState,
  })

  const value = useMemo<CustomerAuthContextValue>(
    () => ({
      token,
      account,
      profile,
      authenticated: Boolean(token && account && profile),
      initializing,
      busy,
      error,
      clearError,
      signup,
      login,
      verifyLoginSecondFactor,
      verifyLoginSecondFactorWithPasskey,
      updateProfile: patchProfile,
      logout,
      refresh,
    }),
    [
      account,
      busy,
      clearError,
      error,
      initializing,
      login,
      logout,
      patchProfile,
      profile,
      refresh,
      signup,
      token,
      verifyLoginSecondFactor,
      verifyLoginSecondFactorWithPasskey,
    ],
  )

  return <CustomerAuthContext.Provider value={value}>{children}</CustomerAuthContext.Provider>
}

export const useCustomerAuth = () => {
  const context = useContext(CustomerAuthContext)
  if (!context) {
    throw new Error('useCustomerAuth deve essere usato dentro CustomerAuthProvider')
  }
  return context
}
